/* eslint-disable */
import { useState, useCallback, useEffect, createElement, useRef, Fragment } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var stripeColors = {
    grey: 'rgb(103, 116, 133)',
    blue: 'rgb(77, 107, 213)',
    cyan: 'rgb(0, 124, 182)',
    green: 'rgb(12, 128, 94)',
    yellow: 'rgb(188, 84, 17)',
    orange: 'rgb(197, 75, 54)',
    red: 'rgb(205, 62, 99)',
    purple: 'rgb(163, 84, 178)',
    violet: 'rgb(127, 99, 192)',
};
var stripeColorsDark = {
    grey: 'rgb(198, 196, 207)',
    blue: 'rgb(119, 141, 223)',
    cyan: 'rgb(75, 167, 210)',
    green: 'rgb(29, 159, 120)',
    yellow: 'rgb(233, 201, 114)',
    orange: 'rgb(237, 133, 115)',
    red: 'rgb(215, 101, 131)',
    purple: 'rgb(200, 153, 209)',
    violet: 'rgb(163, 143, 209)',
};
var colors = {
    light: {
        background: 'white',
        zebra: '#f8f8f8',
        border: '#ddd',
        grey: stripeColors.grey,
        textColor: '#111',
        purple: stripeColors.purple,
        violet: stripeColors.violet,
        link: stripeColors.blue,
        blue: stripeColors.blue,
        cyan: stripeColors.cyan,
        green: stripeColors.green,
        yellow: stripeColors.yellow,
        orange: stripeColors.orange,
        red: stripeColors.red,
    },
    dark: {
        red: stripeColorsDark.red,
        orange: stripeColorsDark.orange,
        yellow: stripeColorsDark.yellow,
        green: stripeColorsDark.green,
        cyan: stripeColorsDark.cyan,
        blue: stripeColorsDark.blue,
        link: stripeColorsDark.blue,
        violet: stripeColorsDark.violet,
        purple: stripeColorsDark.purple,
        background: '#0a0a0a',
        zebra: '#1a1a1a',
        border: '#555',
        grey: stripeColorsDark.grey,
        textColor: 'white',
    }
};
var complimentaryColors = {
    background: 'textColor',
    blue: 'background',
    border: 'textColor',
    cyan: 'background',
    green: 'background',
    grey: 'background',
    link: 'background',
    orange: 'background',
    purple: 'background',
    red: 'background',
    textColor: 'background',
    violet: 'background',
    yellow: 'background',
    zebra: 'textColor',
};

var focusStyle = "\n:focus {\n  outline: none;\n  box-shadow: 0 0 0 calc(var(--clear-unit) / 4) var(--clear-background),\n    0 0 0 calc(var(--clear-unit) / 2) var(--clear-link);\n}\n::-moz-focus-inner {\n  border:0;\n}\n";
var errorFocusStyle = "\n:focus {\n  outline: none;\n  box-shadow: 0 0 0 calc(var(--clear-unit) / 4) var(--clear-background),\n    0 0 0 calc(var(--clear-unit) / 2) var(--clear-red);\n}\n::-moz-focus-inner {\n  border:0;\n}\n";

var unit = 8;
var breakpoints = {
    xs: 600,
    sm: 960,
    md: 1280,
    lg: 1920,
};
var widths = {
    main: 95 * unit,
    lg: Math.pow(unit, 3) * 2,
    xl: Math.pow(unit, 3) * 4,
};
var numbers = {
    breakpoint: breakpoints,
    fontSize: {
        default: unit * 2,
        label: unit * 1.75,
    },
    width: widths,
    unit: unit,
};
/**
 * returns a CSS string formatted like:
 *
 * ```ts
 * `@media (${minMax}-width: ${breakpoint[width]}px) {
 *  ${styles}
 * }`
 * ```
 *
 * https://chadlavi.github.io/clear/#/helpers#makemediaquery
 */
var makeMediaQuery = function (minMax, width) { return function (styles) { return ("@media (" + minMax + "-width: " + breakpoints[width] + "px) {\n    " + styles + "\n  }"); }; };
// convenience functions
/**
 * insert a CSS style when the browser is narrower than the XS breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#underxs
 */
var underXs = makeMediaQuery('max', 'xs');
/**
 * insert a CSS style when the browser is narrower than the SM breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#undersm
 */
var underSm = makeMediaQuery('max', 'sm');
/**
 * insert a CSS style when the browser is narrower than the MD breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#undermd
 */
var underMd = makeMediaQuery('max', 'md');
/**
 * insert a CSS style when the browser is narrower than the LG breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#underlg
 */
var underLg = makeMediaQuery('max', 'lg');
/**
 * insert a CSS style when the browser is wider than the XS breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#overxs
 */
var overXs = makeMediaQuery('min', 'xs');
/**
 * insert a CSS style when the browser is wider than the SM breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#oversm
 */
var overSm = makeMediaQuery('min', 'sm');
/**
 * insert a CSS style when the browser is wider than the MD breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#overmd
 */
var overMd = makeMediaQuery('min', 'md');
/**
 * insert a CSS style when the browser is wider than the LG breakpoint
 *
 * https://chadlavi.github.io/clear/#/helpers#overlg
 */
var overLg = makeMediaQuery('min', 'lg');

/**
 * Creates a set of css variables necessary for global colors. This component
 * should be included once at the top level of your application.
 *
 * https://chadlavi.github.io/clear/#/global-styles#cssvariables
 */
var CSSVariables = createGlobalStyle(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  :root {\n    ", "\n\n    --clear-unit: ", "px;\n    --clear-main-width: ", "px;\n    --clear-font-size-default: ", "px;\n    --clear-font-size-label: ", "px;\n\n    ", "\n\n    --clear-font-family: -apple-system, BlinkMacSystemFont, avenir next, avenir, helvetica neue, helvetica, Ubuntu,\n      roboto, noto, segoe ui, arial, sans-serif;\n    --clear-monospace-font-family: Menlo, Consolas, Monaco, Liberation Mono, Lucida Console, monospace;\n  }\n\n  @media (prefers-color-scheme: dark) {\n    :root {\n      ", "\n    }\n  }\n  "], ["\n  :root {\n    ", "\n\n    --clear-unit: ", "px;\n    --clear-main-width: ", "px;\n    --clear-font-size-default: ", "px;\n    --clear-font-size-label: ", "px;\n\n    ",
    "\n\n    --clear-font-family: -apple-system, BlinkMacSystemFont, avenir next, avenir, helvetica neue, helvetica, Ubuntu,\n      roboto, noto, segoe ui, arial, sans-serif;\n    --clear-monospace-font-family: Menlo, Consolas, Monaco, Liberation Mono, Lucida Console, monospace;\n  }\n\n  @media (prefers-color-scheme: dark) {\n    :root {\n      ", "\n    }\n  }\n  "])), Object.keys(colors.light).map(function (c) { return "--clear-" + c + ": " + colors.light[c] + ";"; }), numbers.unit, numbers.width.main, numbers.fontSize.default, numbers.fontSize.label, Object.keys(numbers.breakpoint).map(function (k) { return "--clear-breakpoint-" + k + ": " + numbers.breakpoint[k] + "px;"; }), Object.keys(colors.dark).map(function (c) { return "--clear-" + c + ": " + colors.dark[c] + ";"; }));
var templateObject_1;

/**
 * Applies styles to the `<body>` of your app.
 *
 * https://chadlavi.github.io/clear/#/global-styles#globalstyles
 */
var GlobalStyles = createGlobalStyle(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  body {\n    margin: 0;\n    background-color: var(--clear-background);\n    color: var(--clear-textColor);\n    font-family: var(--clear-font-family);\n    font-size: var(--clear-font-size-default);\n    text-rendering: geometricPrecision;\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n    -webkit-text-size-adjust: 100%;\n  }\n"], ["\n  body {\n    margin: 0;\n    background-color: var(--clear-background);\n    color: var(--clear-textColor);\n    font-family: var(--clear-font-family);\n    font-size: var(--clear-font-size-default);\n    text-rendering: geometricPrecision;\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n    -webkit-text-size-adjust: 100%;\n  }\n"])));
var templateObject_1$1;

/**
 * This basic React hook returns a boolean value that reflects whether or not the given query matches.
 *
 * https://chadlavi.github.io/clear/#/helpers#usemediaquery
 */
var useMediaQuery = function (query) {
    var _a = useState(matchMedia(query).matches), result = _a[0], setResult = _a[1];
    var callback = useCallback(function (matchMediaResult) { return setResult(matchMediaResult.matches); }, [setResult]);
    useEffect(function () {
        var matchMediaResult = matchMedia(query);
        callback(matchMediaResult);
        matchMediaResult.addListener(callback);
        return function () { return matchMediaResult.removeListener(callback); };
    }, [callback]);
    return result;
};
/**
 * Returns a boolean value that reflects whether or not the user's viewport is smaller than the given breakpoint.
 *
 * https://chadlavi.github.io/clear/#/helpers#usebreakpoint
 */
var useBreakpoint = function (breakpoint) { return useMediaQuery("(max-width: " + numbers.breakpoint[breakpoint] + "px)"); };
/**
 * Returns a boolean value that reflects whether or not the user's browser is currently in dark mode.
 *
 * https://chadlavi.github.io/clear/#/helpers#usedarkmode
 */
var useDarkMode = function () { return useMediaQuery('(prefers-color-scheme: dark)'); };
/**
 * Returns a boolean value that reflects whether or not the user's browser is currently in dark mode.
 *
 * https://chadlavi.github.io/clear/#/helpers#useresponsivecolor
 */
var useResponsiveColor = function (color) {
    var theme = useDarkMode() ? 'dark' : 'light';
    return colors[theme][color];
};

var ButtonBase = function (_a) {
    var destructive = _a.destructive, primary = _a.primary, props = __rest(_a, ["destructive", "primary"]);
    return (createElement("button", __assign({}, props, { className: "" + props.className + (destructive ? ' destructive' : '') + (primary ? ' primary' : '') + (props.disabled ? ' disabled' : '') + "\n      " })));
};
/**
 * A simple styled `<button>`
 *
 * https://chadlavi.github.io/clear/#/button
 */
var Button = styled(ButtonBase)(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n  background: var(--clear-border);\n  border-radius: var(--clear-unit);\n  border: 1px solid var(--clear-border);\n  color: var(--clear-textColor);\n  ", ";\n  ", ";\n  cursor: pointer;\n  ", ";\n  font-size: var(--clear-font-size-default);\n  margin-bottom: calc(var(--clear-unit) / 2);\n  margin-left: 0;\n  margin-right: 0;\n  margin-top: calc(var(--clear-unit) / 2);\n  padding: calc(var(--clear-unit) * 1.5) calc(var(--clear-unit) * 3);\n  ", "\n"], ["\n  background: var(--clear-border);\n  border-radius: var(--clear-unit);\n  border: 1px solid var(--clear-border);\n  color: var(--clear-textColor);\n  ",
    ";\n  ",
    ";\n  cursor: pointer;\n  ",
    ";\n  font-size: var(--clear-font-size-default);\n  margin-bottom: calc(var(--clear-unit) / 2);\n  margin-left: 0;\n  margin-right: 0;\n  margin-top: calc(var(--clear-unit) / 2);\n  padding: calc(var(--clear-unit) * 1.5) calc(var(--clear-unit) * 3);\n  ", "\n"])), function (p) { return p.primary ? "\n    background: var(--clear-link);\n    border: 1px solid var(--clear-link);\n    color: var(--clear-background);\n  " : ''; }, function (p) { return p.destructive ? "\n    background: var(--clear-red);\n    border: 1px solid var(--clear-red);\n    color: var(--clear-background);\n  " : ''; }, function (p) { return p.disabled ? "\n    cursor: not-allowed;\n    opacity: 0.5;\n  " : ''; }, function (p) { return p.destructive ? errorFocusStyle : focusStyle; });
var templateObject_1$2;

var Wrapper = styled.div(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n  align-items: center;\n  display: flex;\n  margin: var(--clear-unit);\n"], ["\n  align-items: center;\n  display: flex;\n  margin: var(--clear-unit);\n"])));
var InputWrapper = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: block;\n  height: var(--clear-font-size-label);\n  width: var(--clear-font-size-label);\n  position: relative;\n"], ["\n  display: block;\n  height: var(--clear-font-size-label);\n  width: var(--clear-font-size-label);\n  position: relative;\n"])));
var InputBase = function (_a) {
    var error = _a.error, _label = _a.label, props = __rest(_a, ["error", "label"]);
    return (createElement("input", __assign({}, props, { type: 'checkbox', className: "" + props.className + (error ? ' error' : '') })));
};
var StyledInput = styled(InputBase)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  -webkit-appearance: none;\n  background: var(--clear-", ");\n  border-radius: calc(var(--clear-unit) / 4);\n  border: 1px solid var(--clear-", ");\n  display: block;\n  height: var(--clear-font-size-label);\n  margin: 0;\n  width: var(--clear-font-size-label);\n  opacity: ", ";\n  cursor: ", ";\n  ", "\n  ", "\n"], ["\n  -webkit-appearance: none;\n  background: var(--clear-", ");\n  border-radius: calc(var(--clear-unit) / 4);\n  border: 1px solid var(--clear-", ");\n  display: block;\n  height: var(--clear-font-size-label);\n  margin: 0;\n  width: var(--clear-font-size-label);\n  opacity: ", ";\n  cursor: ", ";\n  ", "\n  ", "\n"])), function (p) { return p.checked ? (p.error ? 'red' : 'link') : 'background'; }, function (p) { return p.error ? 'red' : (p.checked ? 'link' : 'textColor'); }, function (p) { return p.disabled ? '0.5' : '1'; }, function (p) { return p.disabled ? 'not-allowed' : 'pointer'; }, focusStyle, function (p) { return p.error ? errorFocusStyle : ''; });
var SVGBase = function (_a) {
    var _checked = _a.checked, props = __rest(_a, ["checked"]);
    return (createElement("svg", __assign({ version: '1.1', xmlns: 'http://www.w3.org/2000/svg', x: '0', y: '0' }, props)));
};
var Icon = styled(SVGBase)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  fill: none;\n  stroke: var(--clear-background);\n  stroke-width: calc(var(--clear-unit) / 4 + 1px);\n  pointer-events: none;\n  position: absolute;\n  top: 0;\n  left: 0;\n  display: ", "\n"], ["\n  fill: none;\n  stroke: var(--clear-background);\n  stroke-width: calc(var(--clear-unit) / 4 + 1px);\n  pointer-events: none;\n  position: absolute;\n  top: 0;\n  left: 0;\n  display: ", "\n"])), function (p) { return p.checked ? 'block' : 'none'; });
var LabelBase = function (_a) {
    var disabled = _a.disabled, error = _a.error, props = __rest(_a, ["disabled", "error"]);
    return (createElement("label", __assign({}, props, { className: "" + props.className + (error ? ' error' : '') + (disabled ? ' disabled' : '') })));
};
var StyledLabel = styled(LabelBase)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ", ";\n  opacity: ", ";\n  cursor: ", ";\n  margin-left: var(--clear-unit);\n"], ["\n  ", ";\n  opacity: ", ";\n  cursor: ", ";\n  margin-left: var(--clear-unit);\n"
    /**
     * A wrapper around HTML `<input type='checkbox'>`.
     *
     * https://chadlavi.github.io/clear/#/input#checkbox
     */
])), function (p) { return p.error ? 'color: var(--clear-red)' : ''; }, function (p) { return p.disabled ? '0.5' : '1'; }, function (p) { return p.disabled ? 'not-allowed' : 'pointer'; });
/**
 * A wrapper around HTML `<input type='checkbox'>`.
 *
 * https://chadlavi.github.io/clear/#/input#checkbox
 */
var Checkbox = function (props) {
    var error = props.error, label = props.label, other = __rest(props, ["error", "label"]);
    return (createElement(Wrapper, null,
        createElement(InputWrapper, null,
            createElement(StyledInput, __assign({ error: error }, other)),
            createElement(Icon, { checked: other.checked, viewBox: '0 0 24 24' },
                createElement("polyline", { points: '20 6 9 17 4 12' }))),
        label &&
            createElement(StyledLabel, { disabled: other.disabled, error: error, htmlFor: other.id }, label)));
};
var templateObject_1$3, templateObject_2, templateObject_3, templateObject_4, templateObject_5;

/**
 * A simple styled `<code>`
 *
 * https://chadlavi.github.io/clear/#/text#code
 */
var Code = styled('code')(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n  font-family: var(--clear-monospace-font-family);\n  font-size: 0.9em;\n  display: inline;\n  margin: 0;\n  background: var(--clear-zebra);\n  padding: calc(1em / 8) calc(1em / 8 + 1px);\n  border-radius: calc(1em / 4);\n"], ["\n  font-family: var(--clear-monospace-font-family);\n  font-size: 0.9em;\n  display: inline;\n  margin: 0;\n  background: var(--clear-zebra);\n  padding: calc(1em / 8) calc(1em / 8 + 1px);\n  border-radius: calc(1em / 4);\n"])));
var templateObject_1$4;

/**
 * A simple styled `<pre>` for formatting code blocks
 *
 * https://chadlavi.github.io/clear/#/text#codeblock
 */
var CodeBlock = styled('pre')(templateObject_1$5 || (templateObject_1$5 = __makeTemplateObject(["\n  font-family: var(--clear-monospace-font-family);\n  font-size: 0.9em;\n  line-height: 1.5;\n  display: block;\n  margin-block-end: calc(var(--clear-unit) * 2);\n  margin-block-start: calc(var(--clear-unit) * 2);\n  background: var(--clear-zebra);\n  padding: calc(var(--clear-unit) * 2);\n  border-radius: var(--clear-unit);\n  overflow-x: auto;\n"], ["\n  font-family: var(--clear-monospace-font-family);\n  font-size: 0.9em;\n  line-height: 1.5;\n  display: block;\n  margin-block-end: calc(var(--clear-unit) * 2);\n  margin-block-start: calc(var(--clear-unit) * 2);\n  background: var(--clear-zebra);\n  padding: calc(var(--clear-unit) * 2);\n  border-radius: var(--clear-unit);\n  overflow-x: auto;\n"])));
var templateObject_1$5;

/**
 * A simple styled `<h1>`
 *
 * Can also be used to create any header (`h1` through `h6`):
 *
 * ```tsx
 * <Header as='h2'>
 *  This will render in the DOM as an `<h2>`
 * </Header>
 * ```
 *
 * https://chadlavi.github.io/clear/#/text#header
 */
var Header = styled('h1')(templateObject_1$6 || (templateObject_1$6 = __makeTemplateObject([""], [""])));
var templateObject_1$6;

var ParagraphBase = function (_a) {
    var _margins = _a.margins, props = __rest(_a, ["margins"]);
    return createElement("p", __assign({}, props));
};
/**
 * A simple styled `<p>`
 *
 * https://chadlavi.github.io/clear/#/text#paragraph
 */
var Paragraph = styled(ParagraphBase)(templateObject_1$7 || (templateObject_1$7 = __makeTemplateObject(["\n  line-height: 1.5;\n  ", "\n"], ["\n  line-height: 1.5;\n  ",
    "\n"])), function (p) { return p.margins === false ? "\n    margin-block-start: 0;\n    margin-block-end: 0;\n  " : "\n    margin-block-start: 1em;\n    margin-block-end: 1em;\n  "; });
var templateObject_1$7;

/**
 * A React hook to listen for clickaway and run a callback function when it happens.
 *
 * Use this hook to set the value of a React `ref` prop.
 *
 * @param onClickAway Callback function to run when clickaway happens
 * @param shouldListen Optional boolean switch to prevent listening
 */
function useClickaway(onClickAway, shouldListen) {
    var ref = useRef(null);
    var handleClickOutside = function (event) {
        if (event.target) {
            if (ref.current) {
                if (!ref.current.contains(event.target)) {
                    onClickAway();
                }
            }
        }
    };
    useEffect(function () {
        if (shouldListen !== false) {
            document.addEventListener('click', handleClickOutside, true);
            return function () {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }
    });
    return ref;
}

var uuid = function () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0;
        var v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

var Backdrop = styled('div')(templateObject_1$8 || (templateObject_1$8 = __makeTemplateObject(["\n  align-items: center;\n  background-color: hsl(250, 60%, 3%, 0.7);\n  display: flex;\n  height: 100%;\n  justify-content: center;\n  left: 0;\n  position: fixed;\n  top: 0;\n  width: 100%;\n"], ["\n  align-items: center;\n  background-color: hsl(250, 60%, 3%, 0.7);\n  display: flex;\n  height: 100%;\n  justify-content: center;\n  left: 0;\n  position: fixed;\n  top: 0;\n  width: 100%;\n"])));
var DialogBody = styled('div')(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n  background: var(--clear-background);\n  border-radius: calc(var(--clear-unit) * 2);\n  border: 1px solid var(--clear-border);\n  @media (min-width: ", "px) {\n    max-width: calc(var(--clear-main-width) - var(--clear-unit) * 4);\n  }\n  max-height: calc(100% - var(--clear-unit) * 4);\n  max-width: calc(100% - var(--clear-unit) * 4);\n  padding: calc(var(--clear-unit) * 2);\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  ", "\n"], ["\n  background: var(--clear-background);\n  border-radius: calc(var(--clear-unit) * 2);\n  border: 1px solid var(--clear-border);\n  @media (min-width: ", "px) {\n    max-width: calc(var(--clear-main-width) - var(--clear-unit) * 4);\n  }\n  max-height: calc(100% - var(--clear-unit) * 4);\n  max-width: calc(100% - var(--clear-unit) * 4);\n  padding: calc(var(--clear-unit) * 2);\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  ",
    "\n"])), numbers.width.main, underXs("\n    border: none;\n    width: 100%;\n    max-width: 100%;\n    height: 100%;\n    max-height: 100%;\n    border-radius: 0;\n    margin: 0;\n  "));
var DialogContentBase = function (_a) {
    var _margin = _a.margin, props = __rest(_a, ["margin"]);
    return (createElement("div", __assign({}, props)));
};
var DialogContent = styled(DialogContentBase)(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n  & > ", ":first-child, & > ", ":first-child {\n    margin-block-start: 0;\n    margin-top: 0;\n  }\n  overflow-y: auto;\n  ", "\n"], ["\n  & > ", ":first-child, & > ", ":first-child {\n    margin-block-start: 0;\n    margin-top: 0;\n  }\n  overflow-y: auto;\n  ", "\n"])), Header, Paragraph, function (p) { return p.margin ? 'margin-block-end: calc(var(--clear-unit) * 3)' : ''; });
var DialogButtons = styled('div')(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row-reverse;\n  flex-shrink: 0;\n  ", " {\n    margin-left: calc(var(--clear-unit) / 2);\n    margin-right: calc(var(--clear-unit) / 2);\n  }\n"], ["\n  display: flex;\n  flex-direction: row-reverse;\n  flex-shrink: 0;\n  ", " {\n    margin-left: calc(var(--clear-unit) / 2);\n    margin-right: calc(var(--clear-unit) / 2);\n  }\n"])), Button);
var BackupButton = styled(DialogButtons)(templateObject_5$1 || (templateObject_5$1 = __makeTemplateObject(["\n  display: none;\n  margin-block-start: calc(var(--clear-unit) * 3);\n  ", "\n"], ["\n  display: none;\n  margin-block-start: calc(var(--clear-unit) * 3);\n  ",
    "\n"
    /**
     * A simple responsive modal with optional action buttons
     *
     * https://chadlavi.github.io/clear/#/dialog
     */
])), underXs("\n    display: flex;\n  "));
/**
 * A simple responsive modal with optional action buttons
 *
 * https://chadlavi.github.io/clear/#/dialog
 */
var Dialog = function (props) {
    var actions = props.actions, disableClickAway = props.disableClickAway, open = props.open, setOpen = props.setOpen, header = props.header, other = __rest(props, ["actions", "disableClickAway", "open", "setOpen", "header"]);
    var handleOnKeyDown = function (e) {
        if (e.keyCode === 27 || e.key === 'Escape') {
            if (disableClickAway) {
                e.stopPropagation();
            }
            else {
                setOpen(false);
            }
        }
    };
    var backdropRef = useRef(null);
    var dialogRef = useClickaway(function () { return setOpen(false); }, disableClickAway !== true);
    useEffect(function () {
        var _a;
        (_a = backdropRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (open) {
            document.addEventListener('keydown', handleOnKeyDown, true);
            return function () {
                document.removeEventListener('keydown', handleOnKeyDown, true);
            };
        }
    }, [open]);
    return (createElement(Fragment, null, open &&
        createElement(Backdrop, { tabIndex: 0, ref: backdropRef },
            createElement(DialogBody, __assign({}, other, { ref: dialogRef }),
                createElement(DialogContent, { margin: actions !== undefined },
                    header &&
                        createElement(Header, { as: 'h2' }, header),
                    props.children),
                actions ?
                    createElement(DialogButtons, null, actions.map(function (a) { return (createElement(Button, __assign({ key: a.label, primary: a.primary }, a.buttonProps, { onClick: a.onClick }), a.label)); }))
                    : createElement(BackupButton, null,
                        createElement(Button, { onClick: function () { return setOpen(false); } }, "Close"))))));
};
var templateObject_1$8, templateObject_2$1, templateObject_3$1, templateObject_4$1, templateObject_5$1;

var Container = function (_a) {
    var _size = _a.size, props = __rest(_a, ["size"]);
    return createElement("div", __assign({}, props));
};
/**
 * A simple 12-column grid item. Must be used as a direct child of `<Grid>`.
 *
 * https://chadlavi.github.io/clear/#/layout#griditem
 */
var GridItem = styled(Container)(templateObject_1$9 || (templateObject_1$9 = __makeTemplateObject(["\n  flex-basis: calc(100% * ", " / 12);\n  width: calc(100% * ", " / 12);\n  max-width: calc(100% * ", " / 12);\n  @media only screen and (max-width: ", "px) {\n    flex-basis: 100%;\n    width: 100%;\n    max-width: 100%;\n  }\n"], ["\n  flex-basis: calc(100% * ", " / 12);\n  width: calc(100% * ", " / 12);\n  max-width: calc(100% * ", " / 12);\n  @media only screen and (max-width: ", "px) {\n    flex-basis: 100%;\n    width: 100%;\n    max-width: 100%;\n  }\n"])), function (p) { return p.size || 12; }, function (p) { return p.size || 12; }, function (p) { return p.size || 12; }, numbers.breakpoint.xs);
var templateObject_1$9;

var Container$1 = function (_a) {
    var _spacing = _a.spacing, props = __rest(_a, ["spacing"]);
    return createElement("div", __assign({}, props));
};
/**
 * A simple 12-column grid container. Must be used in conjunciton with
 * `<GridItem>`.
 *
 * https://chadlavi.github.io/clear/#/layout#grid
 */
var Grid = styled(Container$1)(templateObject_1$a || (templateObject_1$a = __makeTemplateObject(["\n  display: flex;\n  flex-wrap: wrap;\n  padding: ", "px;\n  & > ", " {\n    padding: ", "px;\n  }\n"], ["\n  display: flex;\n  flex-wrap: wrap;\n  padding: ", "px;\n  & > ", " {\n    padding: ", "px;\n  }\n"])), function (p) { return p.spacing ? p.spacing / 2 : 0; }, GridItem, function (p) { return p.spacing ? p.spacing / 2 : 0; });
var templateObject_1$a;

/**
 * Calls props.onClick, but also selects the contents of the Input on click
 * @param func the onClick passed to `<Input>`
 */
var forwardOnClick = function (func) { return function (e) {
    var t = e.currentTarget;
    if (func)
        func(e);
    if (t.value)
        setTimeout(function () {
            t.select();
        }, 1);
}; };
/**
 * Calls props.onFocus, but also selects the contents of the Input on focus
 * @param func the onFocus passed to `<Input>`
 */
var forwardOnFocus = function (func) { return function (e) {
    var t = e.currentTarget;
    if (func)
        func(e);
    if (t.value)
        setTimeout(function () {
            t.select();
        }, 1);
}; };
var InputBase$1 = function (_a) {
    var error = _a.error, props = __rest(_a, ["error"]);
    return (createElement("input", __assign({}, props, { onClick: forwardOnClick(props.onClick), onFocus: forwardOnFocus(props.onFocus), value: props.value || (props.type === 'number' ? ' ' : ''), className: "" + props.className + (error ? ' error' : '') })));
};
/**
 * A simple styled Input
 *
 * https://chadlavi.github.io/clear/#/input#input
 */
var Input = styled(InputBase$1)(templateObject_1$b || (templateObject_1$b = __makeTemplateObject(["\n  background-color: var(--clear-background);\n  color: var(--clear-", ");\n  border: 1px solid var(--clear-", ");\n  border-radius: var(--clear-unit);\n  padding: calc(var(--clear-unit) * 1.5);\n  font-size: var(--clear-font-size-default);\n  margin: calc(var(--clear-unit) / 2) 0;\n  width: 100%;\n  min-width: 100%;\n  max-width: 100%;\n  -webkit-appearance: none;\n  ", "\n  ", "\n  ", "\n"], ["\n  background-color: var(--clear-background);\n  color: var(--clear-", ");\n  border: 1px solid var(--clear-", ");\n  border-radius: var(--clear-unit);\n  padding: calc(var(--clear-unit) * 1.5);\n  font-size: var(--clear-font-size-default);\n  margin: calc(var(--clear-unit) / 2) 0;\n  width: 100%;\n  min-width: 100%;\n  max-width: 100%;\n  -webkit-appearance: none;\n  ", "\n  ", "\n  ", "\n"])), function (p) { return p.error ? 'red' : 'textColor'; }, function (p) { return p.error ? 'red' : 'border'; }, focusStyle, function (p) { return p.error ? errorFocusStyle : ''; }, function (p) { return p.disabled ? 'cursor: not-allowed;' : ''; });
var templateObject_1$b;

var LabelBase$1 = function (_a) {
    var disabled = _a.disabled, error = _a.error, required = _a.required, props = __rest(_a, ["disabled", "error", "required"]);
    return (createElement("label", __assign({}, props, { className: "" + props.className + (disabled ? ' disabled' : '') + (error ? ' error' : '') }), required ? createElement(Fragment, null,
        props.children,
        ' (Required)') : props.children));
};
/**
 * A simple styled `<label>`
 *
 * https://chadlavi.github.io/clear/#/text#label
 */
var Label = styled(LabelBase$1)(templateObject_1$c || (templateObject_1$c = __makeTemplateObject(["\n  color: ", ";\n  ", ";\n  width: 100%;\n  font-size: var(--clear-font-size-label);\n"], ["\n  color: ", ";\n  ",
    ";\n  width: 100%;\n  font-size: var(--clear-font-size-label);\n"])), function (p) { return p.error ? 'var(--clear-red)' : 'inherit'; }, function (p) { return p.disabled ? "\n    opacity: 0.5;\n  " : ''; });
var templateObject_1$c;

var Anchor = function (props) {
    var _a;
    var isExternal = Boolean((_a = props.href) === null || _a === void 0 ? void 0 : _a.match(/^http/));
    return (createElement("a", __assign({}, props, { target: isExternal ? '_blank' : props.target, rel: isExternal ? 'noopener noreferrer' : props.rel, title: props.title || "Open \"" + props.href + "\"" + (isExternal ? ' in a new Tab' : '') })));
};
/**
 * A simple styled `<a>`
 *
 * https://chadlavi.github.io/clear/#/link
 */
var Link = styled(Anchor)(templateObject_1$d || (templateObject_1$d = __makeTemplateObject(["\n  color: var(--clear-link);\n  ", "\n  &[target='_blank']::after {\n    content: ' [\u2197]';\n  }\n"], ["\n  color: var(--clear-link);\n  ", "\n  &[target='_blank']::after {\n    content: ' [\\u2197]';\n  }\n"])), focusStyle);
var templateObject_1$d;

var ListBase = function (props) { return (createElement("ul", __assign({}, props))); };
/**
 * A simple styled `<ul>`
 *
 * https://chadlavi.github.io/clear/#/list#list
 */
var List = styled(ListBase)(templateObject_1$e || (templateObject_1$e = __makeTemplateObject(["\n  padding-left: calc(var(--clear-unit) * 5);\n  padding-inline-start: calc(var(--clear-unit) * 5);\n  ", "\n"], ["\n  padding-left: calc(var(--clear-unit) * 5);\n  padding-inline-start: calc(var(--clear-unit) * 5);\n  ",
    "\n"])), underXs("\n    padding-left: calc(var(--clear-unit) * 3);\n    padding-inline-start: calc(var(--clear-unit) * 3);\n  "));
var templateObject_1$e;

var ListItemBase = function (props) { return (createElement("li", __assign({}, props))); };
/**
 * A simple styled `<li>`
 *
 * https://chadlavi.github.io/clear/#/list#listitem
 */
var ListItem = styled(ListItemBase)(templateObject_1$f || (templateObject_1$f = __makeTemplateObject(["\n  line-height: 1.25;\n"], ["\n  line-height: 1.25;\n"])));
var templateObject_1$f;

var DEFAULT_COLOR = 'blue';
var setColor = function (p) {
    if (p.error)
        return 'red';
    if (p.success)
        return 'green';
    if (p.color)
        return p.color;
    return DEFAULT_COLOR;
};
var setContrastColor = function (color) {
    return complimentaryColors[color !== null && color !== void 0 ? color : DEFAULT_COLOR];
};
var NotificationBase = function (_a) {
    var _buttonProps = _a.buttonProps, _color = _a.color, _dismissible = _a.dismissible, _error = _a.error, _mini = _a.mini, _open = _a.open, _setOpen = _a.setOpen, _success = _a.success, _timeOut = _a.timeOut, _transient = _a.transient, props = __rest(_a, ["buttonProps", "color", "dismissible", "error", "mini", "open", "setOpen", "success", "timeOut", "transient"]);
    return createElement("div", __assign({}, props));
};
var NotificationWrapper = styled.div(templateObject_1$g || (templateObject_1$g = __makeTemplateObject(["\n  bottom: 0;\n  display: flex;\n  justify-content: center;\n  left: 0;\n  max-width: 100%;\n  padding: calc(var(--clear-unit) * 2);\n  pointer-events: none;\n  position: fixed;\n  width: 100%;\n  z-index: 1500;\n"], ["\n  bottom: 0;\n  display: flex;\n  justify-content: center;\n  left: 0;\n  max-width: 100%;\n  padding: calc(var(--clear-unit) * 2);\n  pointer-events: none;\n  position: fixed;\n  width: 100%;\n  z-index: 1500;\n"])));
var StyledNotification = styled(NotificationBase)(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n  align-items: center;\n  background: var(--clear-", ");\n  border-radius: var(--clear-font-size-default);\n  display: flex;\n  font-size: var(--clear-font-size-default);\n  font-style: normal;\n  font-weight: normal;\n  justify-content: space-between;\n  max-width: ", ";\n  padding: var(--clear-font-size-default);\n  pointer-events: auto;\n  width: ", ";\n  @keyframes slideInFromBottom {\n    0% {\n      transform: translateY(100%);\n    }\n    100% {\n      transform: translateY(0);\n    }\n  }\n  animation: 0.1s ease-out 0s 1 slideInFromBottom;\n"], ["\n  align-items: center;\n  background: var(--clear-", ");\n  border-radius: var(--clear-font-size-default);\n  display: flex;\n  font-size: var(--clear-font-size-default);\n  font-style: normal;\n  font-weight: normal;\n  justify-content: space-between;\n  max-width: ", ";\n  padding: var(--clear-font-size-default);\n  pointer-events: auto;\n  width: ", ";\n  @keyframes slideInFromBottom {\n    0% {\n      transform: translateY(100%);\n    }\n    100% {\n      transform: translateY(0);\n    }\n  }\n  animation: 0.1s ease-out 0s 1 slideInFromBottom;\n"])), function (p) { return setColor(p); }, function (p) { return p.mini ? 'calc(var(--clear-unit) * 24)' : '100%'; }, function (p) { return p.mini ? 'unset' : 'calc(var(--clear-unit) * 64)'; });
var NotificationContent = styled(NotificationBase)(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n  color: var(--clear-", ");\n  flex-grow: 1;\n  text-align: ", ";\n  ", "\n"], ["\n  color: var(--clear-", ");\n  flex-grow: 1;\n  text-align: ", ";\n  ", "\n"])), function (p) { return setContrastColor(p.color); }, function (p) { return p.mini ? 'center' : 'left'; }, function (p) { return (p.mini || p.dismissible === false) ? '' : 'margin-right: calc(var(--clear-unit) * 2);'; });
var DismissButtonBase = function (_a) {
    var _color = _a.color, _error = _a.error, _success = _a.success, props = __rest(_a, ["color", "error", "success"]);
    return createElement(Button, __assign({}, props));
};
var DismissButton = styled(DismissButtonBase)(templateObject_4$2 || (templateObject_4$2 = __makeTemplateObject(["\n  background: none;\n  margin: 0;\n  height: auto;\n  border: 1px solid var(--clear-", ");\n  color: var(--clear-", ");\n  :focus {\n    box-shadow: 0 0 0 calc(var(--clear-unit) / 4) var(--clear-", "),\n    0 0 0 calc(var(--clear-unit) / 2) var(--clear-", ");\n  }\n"], ["\n  background: none;\n  margin: 0;\n  height: auto;\n  border: 1px solid var(--clear-", ");\n  color: var(--clear-", ");\n  :focus {\n    box-shadow: 0 0 0 calc(var(--clear-unit) / 4) var(--clear-", "),\n    0 0 0 calc(var(--clear-unit) / 2) var(--clear-", ");\n  }\n"
    /**
     * Use a `<Notification>` to show users a short, timely message that might
     * automatically disappear and that the user may be able to manually dismiss (or both).
     *
     * https://chadlavi.github.io/clear/#/notification
     */
])), function (p) { return setContrastColor(p.color); }, function (p) { return setContrastColor(p.color); }, function (p) { return setColor(p); }, function (p) { return setContrastColor(p.color); });
/**
 * Use a `<Notification>` to show users a short, timely message that might
 * automatically disappear and that the user may be able to manually dismiss (or both).
 *
 * https://chadlavi.github.io/clear/#/notification
 */
var Notification = function (props) {
    var _a, _b;
    var buttonProps = props.buttonProps, color = props.color, children = props.children, dismissible = props.dismissible, error = props.error, mini = props.mini, open = props.open, setOpen = props.setOpen, success = props.success, timeOut = props.timeOut, transient = props.transient, other = __rest(props, ["buttonProps", "color", "children", "dismissible", "error", "mini", "open", "setOpen", "success", "timeOut", "transient"]);
    var onClose = function () { return setOpen(false); };
    var transientOnClose = function () {
        setTimeout(function () {
            setOpen(false);
        }, timeOut !== null && timeOut !== void 0 ? timeOut : 2000);
    };
    var closeOnEsc = function (e) {
        if (e.keyCode === 27 || e.key === 'Escape') {
            onClose();
        }
    };
    var forwardOnKeyDown = function (f) { return function (e) {
        closeOnEsc(e);
        if (f)
            f(e);
    }; };
    var notificationRef = useClickaway(onClose, open);
    useEffect(function () {
        document.addEventListener('keydown', closeOnEsc, true);
        return function () {
            document.removeEventListener('keydown', closeOnEsc, true);
        };
    });
    return (createElement(Fragment, null, open ?
        createElement(NotificationWrapper, { ref: notificationRef },
            createElement(StyledNotification, __assign({ className: "notification" + (error ? ' error' : '') + (success ? ' success' : ''), color: color, error: error, mini: mini, success: success }, other, { onKeyDown: forwardOnKeyDown(props.onKeyDown) }),
                createElement(NotificationContent, { color: color, dismissible: dismissible, mini: mini, className: 'notification-content' }, children),
                (transient && !mini) && transientOnClose(),
                (mini || dismissible === false)
                    ? transientOnClose()
                    : createElement(DismissButton, __assign({}, buttonProps, { color: color, className: 'notification-dismiss-button', error: error, onClick: (_a = buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.onClick) !== null && _a !== void 0 ? _a : onClose, success: success }), (_b = buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.children) !== null && _b !== void 0 ? _b : 'Okay')))
        : undefined));
};
var templateObject_1$g, templateObject_2$2, templateObject_3$2, templateObject_4$2;

var PageBase = function (_a) {
    var _size = _a.size, props = __rest(_a, ["size"]);
    return (createElement("main", __assign({}, props)));
};
/**
 * A simple styled `<main>`
 *
 * https://chadlavi.github.io/clear/#/layout#page
 */
var Page = styled(PageBase)(templateObject_1$h || (templateObject_1$h = __makeTemplateObject(["\nmargin: 0 auto;\nmargin-bottom: calc(var(--clear-unit) * 15);\npadding: var(--clear-unit);\nwidth: ", "px;\nbackground-color: var(--clear-background);\ncolor: var(--clear-textColor);\nfont-family: var(--clear-font-family);\nfont-size: var(--clear-font-size-default);\n-webkit-font-smoothing: antialiased;\n-moz-osx-font-smoothing: grayscale;\nmax-width: 100%;\n&, & * {\n  box-sizing: border-box;\n}\n"], ["\nmargin: 0 auto;\nmargin-bottom: calc(var(--clear-unit) * 15);\npadding: var(--clear-unit);\nwidth: ", "px;\nbackground-color: var(--clear-background);\ncolor: var(--clear-textColor);\nfont-family: var(--clear-font-family);\nfont-size: var(--clear-font-size-default);\n-webkit-font-smoothing: antialiased;\n-moz-osx-font-smoothing: grayscale;\nmax-width: 100%;\n&, & * {\n  box-sizing: border-box;\n}\n"])), function (p) { var _a; return numbers.width[(_a = p.size) !== null && _a !== void 0 ? _a : 'main']; });
var templateObject_1$h;

var Wrapper$1 = styled.div(templateObject_1$i || (templateObject_1$i = __makeTemplateObject(["\n  align-items: center;\n  display: flex;\n  margin: var(--clear-unit);\n"], ["\n  align-items: center;\n  display: flex;\n  margin: var(--clear-unit);\n"])));
var InputWrapper$1 = styled.div(templateObject_2$3 || (templateObject_2$3 = __makeTemplateObject(["\n  display: block;\n  height: var(--clear-font-size-label);\n  width: var(--clear-font-size-label);\n  position: relative;\n"], ["\n  display: block;\n  height: var(--clear-font-size-label);\n  width: var(--clear-font-size-label);\n  position: relative;\n"])));
var InputBase$2 = function (_a) {
    var error = _a.error, _label = _a.label, props = __rest(_a, ["error", "label"]);
    return (createElement("input", __assign({}, props, { type: 'radio', className: "" + props.className + (error ? ' error' : '') })));
};
var StyledInput$1 = styled(InputBase$2)(templateObject_3$3 || (templateObject_3$3 = __makeTemplateObject(["\n  -webkit-appearance: none;\n  background: var(--clear-", ");\n  border-radius: var(--clear-font-size-label);\n  border: 1px solid var(--clear-", ");\n  display: block;\n  height: var(--clear-font-size-label);\n  margin: 0;\n  width: var(--clear-font-size-label);\n  opacity: ", ";\n  cursor: ", ";\n  ", "\n  ", "\n"], ["\n  -webkit-appearance: none;\n  background: var(--clear-", ");\n  border-radius: var(--clear-font-size-label);\n  border: 1px solid var(--clear-", ");\n  display: block;\n  height: var(--clear-font-size-label);\n  margin: 0;\n  width: var(--clear-font-size-label);\n  opacity: ", ";\n  cursor: ", ";\n  ", "\n  ", "\n"])), function (p) { return p.checked ? (p.error ? 'red' : 'link') : 'background'; }, function (p) { return p.error ? 'red' : (p.checked ? 'link' : 'textColor'); }, function (p) { return p.disabled ? '0.5' : '1'; }, function (p) { return p.disabled ? 'not-allowed' : 'pointer'; }, focusStyle, function (p) { return p.error ? errorFocusStyle : ''; });
var SVGContainer = styled.div(templateObject_4$3 || (templateObject_4$3 = __makeTemplateObject(["\n  pointer-events: none;\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: calc(var(--clear-font-size-label) * 10);\n  height: calc(var(--clear-font-size-label) * 10);\n  transform-origin: top left;\n  padding: 20px;\n  transform: scale(0.1);\n  svg {\n    width: 100%;\n    height: 100%;\n  }\n"], ["\n  pointer-events: none;\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: calc(var(--clear-font-size-label) * 10);\n  height: calc(var(--clear-font-size-label) * 10);\n  transform-origin: top left;\n  padding: 20px;\n  transform: scale(0.1);\n  svg {\n    width: 100%;\n    height: 100%;\n  }\n"])));
var SVGBase$1 = function (_a) {
    var _checked = _a.checked, props = __rest(_a, ["checked"]);
    return (createElement(SVGContainer, null,
        createElement("div", null,
            createElement("svg", __assign({ viewBox: '0 0 100 100' }, props),
                createElement("circle", { cx: '50', cy: '50', r: '40', stroke: 'var(--clear-background)', fill: 'none', strokeWidth: '20' })))));
};
var Icon$1 = styled(SVGBase$1)(templateObject_5$2 || (templateObject_5$2 = __makeTemplateObject(["\n  display: ", "\n"], ["\n  display: ", "\n"])), function (p) { return p.checked ? 'block' : 'none'; });
var LabelBase$2 = function (_a) {
    var disabled = _a.disabled, error = _a.error, props = __rest(_a, ["disabled", "error"]);
    return (createElement("label", __assign({}, props, { className: "" + props.className + (error ? ' error' : '') + (disabled ? ' disabled' : '') })));
};
var StyledLabel$1 = styled(LabelBase$2)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  ", ";\n  opacity: ", ";\n  cursor: ", ";\n  margin-left: var(--clear-unit);\n"], ["\n  ", ";\n  opacity: ", ";\n  cursor: ", ";\n  margin-left: var(--clear-unit);\n"
    /**
     * A wrapper around HTML `<input type='radio'>`.
     *
     * https://chadlavi.github.io/clear/#/input#radio
     */
])), function (p) { return p.error ? 'color: var(--clear-red)' : ''; }, function (p) { return p.disabled ? '0.5' : '1'; }, function (p) { return p.disabled ? 'not-allowed' : 'pointer'; });
/**
 * A wrapper around HTML `<input type='radio'>`.
 *
 * https://chadlavi.github.io/clear/#/input#radio
 */
var Radio = function (props) {
    var error = props.error, label = props.label, other = __rest(props, ["error", "label"]);
    return (createElement(Wrapper$1, { role: 'radio' },
        createElement(InputWrapper$1, { role: 'radio' },
            createElement(StyledInput$1, __assign({ error: error, tabIndex: 0 }, other)),
            createElement(Icon$1, { checked: other.checked })),
        label &&
            createElement(StyledLabel$1, { disabled: other.disabled, error: error, htmlFor: other.id }, label)));
};
var templateObject_1$i, templateObject_2$3, templateObject_3$3, templateObject_4$3, templateObject_5$2, templateObject_6;

var ContainerBase = function (_a) {
    var _contentMinHeight = _a.contentMinHeight, _contentMinWidth = _a.contentMinWidth, _direction = _a.direction, _maxHeight = _a.maxHeight, _maxWidth = _a.maxWidth, props = __rest(_a, ["contentMinHeight", "contentMinWidth", "direction", "maxHeight", "maxWidth"]);
    return createElement("div", __assign({}, props));
};
var Container$2 = styled(ContainerBase)(templateObject_1$j || (templateObject_1$j = __makeTemplateObject(["\n  width: 100%;\n  ", "\n  ", "\n\n  ", "\n"], ["\n  width: 100%;\n  ",
    "\n  ",
    "\n\n  ",
    "\n"])), function (p) {
    var direction = p.direction;
    if (direction === 'horizontal') {
        return 'overflow-x: auto; max-width: 100%;';
    }
    else {
        return 'overflow-y: auto; max-height: 100%;';
    }
}, function (p) {
    var maxHeight = p.maxHeight;
    if (typeof maxHeight === 'number') {
        return "max-height: " + maxHeight + "px;";
    }
    else if (maxHeight) {
        return "max-height: " + maxHeight + ";";
    }
    else {
        return '';
    }
}, function (p) {
    var maxWidth = p.maxWidth;
    if (typeof maxWidth === 'number') {
        return "max-width: " + maxWidth + "px;";
    }
    else if (maxWidth) {
        return "max-width: " + maxWidth + ";";
    }
    else {
        return '';
    }
});
var ContentBase = function (_a) {
    var _contentMinHeight = _a.contentMinHeight, _contentMinWidth = _a.contentMinWidth, _direction = _a.direction, _maxHeight = _a.maxHeight, _maxWidth = _a.maxWidth, props = __rest(_a, ["contentMinHeight", "contentMinWidth", "direction", "maxHeight", "maxWidth"]);
    return createElement("div", __assign({}, props));
};
var Content = styled(ContentBase)(templateObject_2$4 || (templateObject_2$4 = __makeTemplateObject(["\n  ", "\n  ", "\n"], ["\n  ",
    "\n  ",
    "\n"
    /**
     * You can use a ScrollContainer to create a verticall or horizontally scrollable div with content of a fixed minimum
     * height/width. Scroll containers are very convenient ways to prevent <Table>s from becoming unmanageable on
     * responsive devices.
     *
     * https://chadlavi.github.io/clear/#/layout#scrollcontainer
     */
])), function (p) {
    var contentMinHeight = p.contentMinHeight;
    if (typeof contentMinHeight === 'number') {
        return "min-height: " + contentMinHeight + "px;";
    }
    else if (contentMinHeight) {
        return "min-height: " + contentMinHeight + ";";
    }
    else {
        return '';
    }
}, function (p) {
    var contentMinWidth = p.contentMinWidth;
    if (typeof contentMinWidth === 'number') {
        return "min-width: " + contentMinWidth + "px;";
    }
    else if (contentMinWidth) {
        return "min-width: " + contentMinWidth + ";";
    }
    else {
        return '';
    }
});
/**
 * You can use a ScrollContainer to create a verticall or horizontally scrollable div with content of a fixed minimum
 * height/width. Scroll containers are very convenient ways to prevent <Table>s from becoming unmanageable on
 * responsive devices.
 *
 * https://chadlavi.github.io/clear/#/layout#scrollcontainer
 */
var ScrollContainer = function (props) { return (createElement(Container$2, { direction: props.direction, maxHeight: props.maxHeight, maxWidth: props.maxWidth },
    createElement(Content, __assign({ contentMinHeight: props.contentMinHeight, contentMinWidth: props.contentMinWidth }, props)))); };
var templateObject_1$j, templateObject_2$4;

var SelectBase = function (_a) {
    var error = _a.error, props = __rest(_a, ["error"]);
    return (createElement("select", __assign({}, props, { className: "" + props.className + (error ? ' error' : '') })));
};
var StyledSelect = styled(SelectBase)(templateObject_1$k || (templateObject_1$k = __makeTemplateObject(["\n  background-color: var(--clear-background);\n  color: var(--clear-", ");\n  border: 1px solid var(--clear-", ");\n  border-radius: var(--clear-unit);\n  padding: calc(var(--clear-unit) * 1.5);\n  font-size: inherit;\n  margin: calc(var(--clear-unit) / 2) 0;\n  width: 100%;\n  -webkit-appearance: none;\n  ", "\n  ", "\n  cursor: ", ";\n  opacity: ", ";\n"], ["\n  background-color: var(--clear-background);\n  color: var(--clear-", ");\n  border: 1px solid var(--clear-", ");\n  border-radius: var(--clear-unit);\n  padding: calc(var(--clear-unit) * 1.5);\n  font-size: inherit;\n  margin: calc(var(--clear-unit) / 2) 0;\n  width: 100%;\n  -webkit-appearance: none;\n  ", "\n  ", "\n  cursor: ", ";\n  opacity: ", ";\n"])), function (p) { return p.error ? 'red' : 'textColor'; }, function (p) { return p.error ? 'red' : 'border'; }, focusStyle, function (p) { return p.error ? errorFocusStyle : ''; }, function (p) { return p.disabled ? 'not-allowed' : 'pointer'; }, function (p) { return p.disabled ? '0.5' : '1'; });
var SelectOptions = function (_a) {
    var options = _a.options;
    return (createElement(Fragment, null, options.map(function (o) { return (createElement("option", { key: o.label + ":" + o.value, value: o.value }, o.label)); })));
};
/**
 * A simple styled HTML `<select>`
 *
 * https://chadlavi.github.io/clear/#/input#select
 */
var Select = function (props) {
    var multiple = props.multiple, options = props.options, other = __rest(props, ["multiple", "options"]);
    return (createElement(StyledSelect, __assign({}, other, { defaultValue: multiple ? undefined : '', multiple: multiple }),
        multiple ? '' : createElement("option", { hidden: true, disabled: true, value: '' }),
        createElement(SelectOptions, { options: options })));
};
/**
 * handles update logic to update the value of a `<Select>` with `multiple={true}`
 *
 * Requires one argument, the function to set the `<select>` value. Defaults to
 * expecting a React `useState` hook, but you can optionally specify a type for the
 * setter function.
 */
var handleMutliSelectOnChange = function (setter) { return function (e) {
    var updatedOptions = Array.from(e.target.options)
        .filter(function (option) { return option.selected; })
        .map(function (x) { return x.value; });
    setter(updatedOptions);
}; };
var templateObject_1$k;

var jumpTo = function (id) { return function () {
    var el = document.getElementById(id);
    if (el) {
        el.scrollIntoView();
        el.focus();
    }
}; };
var SkipLinkButton = styled(Button)(templateObject_1$l || (templateObject_1$l = __makeTemplateObject(["\n  position: absolute;\n  top: var(--clear-unit);\n  left: var(--clear-unit);\n  opacity: 0;\n  pointer-events: none;\n  user-select: none;\n  :focus {\n    opacity: 1;\n    pointer-events: unset;\n    user-select: unset;\n  }\n  \n"], ["\n  position: absolute;\n  top: var(--clear-unit);\n  left: var(--clear-unit);\n  opacity: 0;\n  pointer-events: none;\n  user-select: none;\n  :focus {\n    opacity: 1;\n    pointer-events: unset;\n    user-select: unset;\n  }\n  \n"])));
var SkipLink = function (_a) {
    var id = _a.id;
    return (createElement(SkipLinkButton, { onClick: jumpTo(id), primary: true }, "Skip to main content"));
};
var templateObject_1$l;

/**
 * A simple styled `<table>`
 *
 * https://chadlavi.github.io/clear/#/table#table
 */
var Table = styled('table')(templateObject_1$m || (templateObject_1$m = __makeTemplateObject(["\n  border-collapse: collapse;\n  flex-basis: 100%;\n  width: 100%;\n  margin-bottom: var(--clear-font-size-default);\n"], ["\n  border-collapse: collapse;\n  flex-basis: 100%;\n  width: 100%;\n  margin-bottom: var(--clear-font-size-default);\n"])));
var templateObject_1$m;

/**
 * A simple styled `<td>`
 *
 * Can also be used to create a `<th>`:
 *
 * ```tsx
 * <TableCell as='th'>
 *  This will be rendered as a `<th>`
 * </TableCell>
 * ```
 *
 * https://chadlavi.github.io/clear/#/table#tablecell
 */
var TableCell = styled('td')(templateObject_1$n || (templateObject_1$n = __makeTemplateObject(["\n  text-align: left;\n  padding: var(--clear-unit);\n"], ["\n  text-align: left;\n  padding: var(--clear-unit);\n"])));
var templateObject_1$n;

/**
 * A simple styled `<thead>`
 *
 * https://chadlavi.github.io/clear/#/table#tablehead
 */
var TableHead = styled('thead')(templateObject_1$o || (templateObject_1$o = __makeTemplateObject(["\n  ", " {\n    font-size: var(--clear-font-size-label);\n    text-align: left;\n    padding: var(--clear-unit);\n    padding-bottom: calc(var(--clear-unit) / 2);\n  }\n"], ["\n  ", " {\n    font-size: var(--clear-font-size-label);\n    text-align: left;\n    padding: var(--clear-unit);\n    padding-bottom: calc(var(--clear-unit) / 2);\n  }\n"])), TableCell);
var templateObject_1$o;

/**
 * A simple styled `<tbody>`
 *
 * https://chadlavi.github.io/clear/#/table#tablebody
 */
var TableBody = styled('tbody')(templateObject_1$p || (templateObject_1$p = __makeTemplateObject([""], [""])));
var templateObject_1$p;

/**
 * A simple styled `<tr>`
 *
 * https://chadlavi.github.io/clear/#/table#tablerow
 */
var TableRow = styled('tr')(templateObject_1$q || (templateObject_1$q = __makeTemplateObject(["\n  &:nth-child(even) {\n    ", " {\n      background-color: var(--clear-zebra);\n    }\n    ", ":first-child {\n      border-radius: var(--clear-unit) 0 0 var(--clear-unit);\n    }\n    ", ":last-child {\n      border-radius: 0 var(--clear-unit) var(--clear-unit) 0;\n    }\n  }\n"], ["\n  &:nth-child(even) {\n    ", " {\n      background-color: var(--clear-zebra);\n    }\n    ", ":first-child {\n      border-radius: var(--clear-unit) 0 0 var(--clear-unit);\n    }\n    ", ":last-child {\n      border-radius: 0 var(--clear-unit) var(--clear-unit) 0;\n    }\n  }\n"])), TableCell, TableCell, TableCell);
var templateObject_1$q;

/**
 * Generates a table using Clear's `<Table>`, `<TableHead>`, `<TableBody>`, `<TableRow>`, and `<TableCell>` components
 * based on the `data` array and `headers` array passed to it.
 *
 * https://chadlavi.github.io/clear/#/table#generatetable
 */
var GenerateTable = function (_a) {
    var data = _a.data, headers = _a.headers;
    return (createElement(Table, null,
        createElement(TableHead, null,
            createElement(TableRow, null, headers.map(function (h, i) { return (createElement(TableCell, { key: h + "-" + i, as: 'th' }, typeof h === 'string' ? "" + h.charAt(0).toUpperCase() + h.slice(1) : h.label || h.key)); }))),
        createElement(TableBody, null, data.map(function (r) { return (createElement(TableRow, { key: JSON.stringify(r) }, headers.map(function (c, i) { return (createElement(TableCell, { key: c + "-" + i }, r[typeof c === 'string' ? c : c.key])); }))); }))));
};

var buildSources = function (source) {
    if (typeof source === 'string') {
        return (createElement("source", { src: source }));
    }
    else if (Array.isArray(source)) {
        return createElement(Fragment, null, source.map(function (s) { return (createElement("source", __assign({ key: s.src }, s))); }));
    }
    else {
        return (createElement("source", __assign({}, source)));
    }
};
/**
 * A wrapper around html `<video>` that accepts all the props that can be passed to the base `<video>` element,
 * plus a few specialized props:
 *
 * * `backupMessage`: define the backup to show if the video is not supported
 * * `giflike`: if true, treat the video like a gif
 * (default the following props to `true`: `autoplay`, `loop`, `mute`, `playInline`)
 * * `source`: Either a simple url string for the video source, or an object representing
 * the props that can be passed to
 * a `<source />`, or an array of those objects
 */
var Video = function (props) {
    var backupMessage = props.backupMessage, giflike = props.giflike, source = props.source, videoProps = __rest(props, ["backupMessage", "giflike", "source"]);
    var giflikeProps = giflike ? {
        autoPlay: true,
        loop: true,
        muted: true,
        playsInline: true,
    } : {};
    return (createElement("video", __assign({}, giflikeProps, videoProps),
        buildSources(source), backupMessage !== null && backupMessage !== void 0 ? backupMessage : createElement(Paragraph, null, "Your browser doesn't support this embedded video.")));
};

export { Button, CSSVariables, Checkbox, Code, CodeBlock, Dialog, GenerateTable, GlobalStyles, Grid, GridItem, Header, Input, Label, Link, List, ListItem, Notification, Page, Paragraph, Radio, ScrollContainer, Select, SkipLink, Table, TableBody, TableCell, TableHead, TableRow, Video, colors, complimentaryColors, errorFocusStyle, focusStyle, handleMutliSelectOnChange, makeMediaQuery, numbers, overLg, overMd, overSm, overXs, stripeColors, stripeColorsDark, underLg, underMd, underSm, underXs, useBreakpoint, useClickaway, useDarkMode, useMediaQuery, useResponsiveColor, uuid };
